import * as Spotlight from "@spotlightjs/spotlight";

// initialize spotlight on for development
if (process.env.NODE_ENV === "development") {
  Spotlight.init();
}

// import the main stylesheet
import "../scss/main.scss";

// import the complete bootstrap framework
import * as bootstrap from "bootstrap";

// import virtual keyboard for POS touch terminals
import { bindVirtualKeyboard } from "./virtual-keyboard";

// import WebSocket client for real-time HTMX component updates
import { bindHtmxComponentWebSocket } from "./htmx-component-websocket";

// import and register htmx to the window element
import htmx from "htmx.org";
window.htmx = htmx;

// Single htmx:afterSwap handler registered once at module level.
htmx.on("htmx:afterSwap", function (event) {
  // HX-Boost does not update body attributes nor classes by default.
  const targetElement = event.detail.target;
  if (targetElement.tagName === "BODY") {
    // parse the response to get the new body attributes
    const parser = new DOMParser();
    const parsedResponse = parser.parseFromString(
      event.detail.xhr.response,
      "text/html",
    );
    const newBodyAttributes =
      parsedResponse.getElementsByTagName("body")[0].attributes;
    // remove all existing attributes from the current body
    Object.values(targetElement.attributes).forEach(({ name }) => {
      targetElement.removeAttribute(name);
    });
    // add all attributes from the body element in the response
    for (const attribute of newBodyAttributes) {
      targetElement.setAttribute(attribute.name, attribute.value);
    }
  }

  // re-bind the instant search component
  bindInstantSearch();
  // re-bind the payment calculator
  bindPaymentCalculator();
  // re-bind the virtual keyboard
  bindVirtualKeyboard();
  // re-bind the WebSocket connection
  bindHtmxComponentWebSocket();
});

// Bind on initial page load
document.addEventListener("DOMContentLoaded", function () {
  bindInstantSearch();
  bindPaymentCalculator();
  bindVirtualKeyboard();
  bindHtmxComponentWebSocket();
});

function bindInstantSearch() {
  let instantSearchInputs = document.getElementsByClassName(
    "instant-search-input",
  );
  Array.from(instantSearchInputs).forEach(function (instantSearchInput) {
    if (instantSearchInput.dataset.bound) return;
    instantSearchInput.dataset.bound = "true";

    instantSearchInput.addEventListener("input", function () {
      const searchValue = instantSearchInput.value.trim().toLowerCase();
      const resultElements = document.querySelectorAll("[data-search-term]");
      Array.from(resultElements).forEach(function (element) {
        element.hidden = !(
          searchValue === "" ||
          element.dataset.searchTerm.toLowerCase().includes(searchValue)
        );
      });
    });
    instantSearchInput.addEventListener("keyup", (event) => {
      if (event.key === "Escape") {
        instantSearchInput.value = "";
        instantSearchInput.dispatchEvent(new Event("input", { bubbles: true }));
      }
    });
  });

  let clearInstantSearchInputs = document.getElementsByClassName(
    "clear-instant-search-input",
  );
  Array.from(clearInstantSearchInputs).forEach(function (clearSearch) {
    if (clearSearch.dataset.bound) return;
    clearSearch.dataset.bound = "true";

    clearSearch.addEventListener("click", function () {
      let instantSearchInputs = document.getElementsByClassName(
        "instant-search-input",
      );
      Array.from(instantSearchInputs).forEach(function (instantSearchInput) {
        instantSearchInput.value = "";
        instantSearchInput.dispatchEvent(new Event("input", { bubbles: true }));
      });
    });
  });
}

// import and register Charts.js to the window element
import Chart from "chart.js/auto";
window.Chart = Chart;

// Payment Calculator
function bindPaymentCalculator() {
  const calculator = document.querySelector(".payment-calculator");
  if (!calculator) return;
  if (calculator.dataset.bound) return;
  calculator.dataset.bound = "true";

  const amountDisplay = document.getElementById("amount-display");
  const changeDisplay = document.getElementById("change-display");

  const totalPrice = parseGerman(calculator.dataset.totalPrice);
  let currentAmount = "0";
  let shortcutJustUsed = false;

  // Format number to German format: 10.5 → "10,50 €"
  function formatGerman(num) {
    return num.toFixed(2).replace(".", ",") + " €";
  }

  // Parse German format to number: "10,50" → 10.5
  function parseGerman(str) {
    return parseFloat(str.replace(",", ".")) || 0;
  }

  // Update all display elements
  function updateDisplay() {
    const amountPaid = parseGerman(currentAmount);
    const change = Math.max(0, amountPaid - totalPrice);

    amountDisplay.textContent = formatGerman(amountPaid);
    changeDisplay.textContent = formatGerman(change);

    // Add green text to change
    if (amountPaid >= totalPrice) {
      changeDisplay.classList.add("text-success", "fw-bold");
    } else {
      changeDisplay.classList.remove("text-success", "fw-bold");
    }
  }

  // Add digit to current amount
  function addDigit(digit) {
    // If shortcut was just used and user presses a digit (not comma), start fresh
    if (shortcutJustUsed && digit !== ",") {
      currentAmount = digit;
      shortcutJustUsed = false;
      updateDisplay();
      return;
    }
    shortcutJustUsed = false;

    // Remove leading zero (except when adding comma)
    if (currentAmount === "0" && digit !== ",") {
      currentAmount = digit;
    } else {
      // Check if comma already exists
      if (digit === "," && currentAmount.includes(",")) {
        return;
      }

      // Check maximum 2 decimal places after comma
      const parts = currentAmount.split(",");
      if (parts.length === 2 && parts[1].length >= 2 && digit !== ",") {
        return;
      }

      currentAmount += digit;
    }
    updateDisplay();
  }

  // Clear amount
  function clearAmount() {
    currentAmount = "0";
    shortcutJustUsed = false;
    updateDisplay();
  }

  // Backspace
  function backspace() {
    shortcutJustUsed = false;
    if (currentAmount.length > 1) {
      currentAmount = currentAmount.slice(0, -1);
    } else {
      currentAmount = "0";
    }
    updateDisplay();
  }

  // Set amount to exact value
  function setAmount(value) {
    currentAmount = value.toFixed(2).replace(".", ",");
    shortcutJustUsed = true;
    updateDisplay();
  }

  // Bind keypad buttons
  calculator.querySelectorAll(".keypad-btn").forEach((button) => {
    button.addEventListener("click", () => {
      const digit = button.dataset.digit;
      const action = button.dataset.action;

      if (digit) {
        addDigit(digit);
      } else if (action === "clear") {
        clearAmount();
      } else if (action === "backspace") {
        backspace();
      }
    });
  });

  // Bind shortcut buttons
  calculator.querySelectorAll(".shortcut-btn").forEach((button) => {
    button.addEventListener("click", () => {
      const shortcut = parseFloat(button.dataset.shortcut);
      setAmount(shortcut);
    });
  });

  // Keyboard support
  document.addEventListener("keydown", function handleKeydown(event) {
    // Only handle keys when the calculator is still in the DOM
    if (!document.contains(calculator)) {
      document.removeEventListener("keydown", handleKeydown);
      return;
    }

    // Ignore if user is typing in an input/textarea
    const tag = event.target.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;

    const key = event.key;

    if (key >= "0" && key <= "9") {
      event.preventDefault();
      addDigit(key);
    } else if (key === "," || key === ".") {
      event.preventDefault();
      addDigit(",");
    } else if (key === "Backspace") {
      event.preventDefault();
      backspace();
    } else if (key === "Escape" || key === "Delete") {
      event.preventDefault();
      clearAmount();
    }
  });

  // Initial display update
  updateDisplay();
}
